import useApiRequest, { ApiResponse } from "@/lib/hooks/use-request";
import {
  CreateSalesRankRequestDto,
  SalesRankResponseDto,
  UpdateSalesRankRequestDto,
} from "@/lib/interfaces/sales-ranks";
import { Button } from "@/ui-components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trophy } from "lucide-react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

const SalesRankEditor = ({ item, onSubmitDone }: { item?: SalesRankResponseDto; onSubmitDone?: () => void }) => {
  const { apiRequest } = useApiRequest();
  const navigate = useNavigate();

  const salesRankSchema = z.object({
    title: z
      .string()
      .min(1, {
        message: "Titel ist erforderlich",
      })
      .max(100, {
        message: "Titel darf maximal 100 Zeichen lang sein",
      }),
    provision: z
      .string()
      .min(1, {
        message: "Provision ist erforderlich",
      })
      .regex(/^\d+$/, {
        message: "Ungültige Provision. Bitte geben Sie eine ganze Zahl ein.",
      }),
    salesUntilNextRank: z
      .string()
      .min(1, {
        message: "Verkäufe bis zum nächsten Rang ist erforderlich",
      })
      .regex(/^\d+$/, {
        message: "Ungültige Anzahl. Bitte geben Sie eine ganze Zahl ein.",
      }),
  });

  type SalesRankFormSchema = z.infer<typeof salesRankSchema>;

  const form = useForm<SalesRankFormSchema>({
    resolver: zodResolver(salesRankSchema),
    defaultValues: {
      title: item?.title || "",
      provision: item?.provision ? item?.provision.toString() : "",
      salesUntilNextRank: item?.salesUntilNextRank ? item?.salesUntilNextRank.toString() : "",
    },
  });

  const onSubmit = async (data: SalesRankFormSchema) => {
    let res: ApiResponse<SalesRankResponseDto>;
    if (item) {
      // Update existing sales rank
      const req: UpdateSalesRankRequestDto = {
        title: data.title !== item.title ? data.title : undefined,
        provision: data.provision.toString() !== item.provision.toString() ? parseInt(data.provision) : undefined,
        salesUntilNextRank: data.salesUntilNextRank.toString() !== item.salesUntilNextRank.toString() 
          ? parseInt(data.salesUntilNextRank) 
          : undefined,
      };

      res = await apiRequest<SalesRankResponseDto>(`settings/sales-ranks/${item._id}`, "PATCH", {
        body: req,
        toast: { toastText: "Verkaufsrang erfolgreich aktualisiert" },
      });
    } else {
      // Create new sales rank
      const req: CreateSalesRankRequestDto = {
        title: data.title,
        provision: parseInt(data.provision),
        salesUntilNextRank: parseInt(data.salesUntilNextRank),
      };

      res = await apiRequest("settings/sales-ranks", "POST", {
        body: req,
        toast: { toastText: "Verkaufsrang erfolgreich erstellt" },
      });
    }
    if (res.data) {
      if (onSubmitDone) {
        onSubmitDone();
      }
      navigate(`/settings/sales-ranks/${res.data._id}`);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5 h-full">
        <div className="flex flex-col gap-2">
          <FormLabel className="text-[16px] font-bold">Verkaufsrang Titel:*</FormLabel>
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem className="flex flex-col bg-white rounded-lg">
                <FormControl>
                  <Input {...field} placeholder="Verkaufsrang Titel" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col gap-2">
          <FormLabel className="text-[16px] font-bold">Provision:*</FormLabel>
          <FormField
            control={form.control}
            name="provision"
            render={({ field }) => (
              <FormItem className="flex flex-col bg-white rounded-lg">
                <FormControl>
                  <Input {...field} placeholder="Provision" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col gap-2">
          <FormLabel className="text-[16px] font-bold">Verkäufe bis zum nächsten Rang:*</FormLabel>
          <FormField
            control={form.control}
            name="salesUntilNextRank"
            render={({ field }) => (
              <FormItem className="flex flex-col bg-white rounded-lg">
                <FormControl>
                  <Input {...field} placeholder="Verkäufe bis zum nächsten Rang" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <Button type="submit" variant="default" className="text-white font-bold flex items-center gap-4 w-fit self-end">
          <Trophy /> <span>{item ? "Verkaufsrang bearbeiten" : "Verkaufsrang erstellen"}</span>
        </Button>
      </form>
    </Form>
  );
};

export default SalesRankEditor;
